<script setup lang="ts">
import {
  formatTimerDuration,
  normalizeTimerDuration,
} from "#bb/lib/timer";

const { t } = useI18n({ useScope: "local" });
const props = defineProps<{ id: string }>();

const { field } = useInlineBlockEditor(props.id);

const mode = field<string>("mode", "countdown");
const durationHours = field<number>("durationHours", 0);
const durationMinutes = field<number>("durationMinutes", 5);
const durationSeconds = field<number>("durationSeconds", 0);
const label = field<string>("label", "Time remaining");
const displayMode = field<string>("displayMode", "progress");
const persistenceMode = field<string>("persistenceMode", "wall-clock");
const initialStatus = field<string>("initialStatus", "running");
const validateForm = field<boolean>("validateForm", false);
const automationPath = field<string>("automationPath", "");
const navigationPath = field<string>("navigationPath", "");
const redirectDelaySeconds = field<number>("redirectDelaySeconds", 0);

const durationMs = computed(() =>
  normalizeTimerDuration(
    (Number(durationHours.value) || 0) * 3_600_000 +
      (Number(durationMinutes.value) || 0) * 60_000 +
      (Number(durationSeconds.value) || 0) * 1000,
  ),
);
const previewTime = computed(() =>
  formatTimerDuration(mode.value === "count-up" ? 0 : durationMs.value),
);
const durationHint = computed(() =>
  durationMs.value === 0 ? t("zeroDurationHint") : t("durationHint"),
);
</script>

<template>
  <EditorSection :title="t('timerSection')" icon="lucide:timer" accent="content">
    <EditorField :label="t('modeLabel')">
      <Select :model-value="mode" @update:model-value="(value) => (mode = value as string)">
        <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="countdown">{{ t("countdown") }}</SelectItem>
          <SelectItem value="count-up">{{ t("countUp") }}</SelectItem>
        </SelectContent>
      </Select>
    </EditorField>

    <EditorField :label="t('durationLabel')" :hint="durationHint">
      <div class="grid grid-cols-3 gap-2">
        <div class="space-y-1">
          <Input
            v-model.number="durationHours"
            type="number"
            min="0"
            step="1"
            class="h-8 rounded-sm bg-muted text-[13px]"
            :aria-label="t('hours')"
          />
          <p class="text-center text-[10.5px] uppercase tracking-wide text-muted-foreground">
            {{ t("hoursShort") }}
          </p>
        </div>
        <div class="space-y-1">
          <Input
            v-model.number="durationMinutes"
            type="number"
            min="0"
            max="59"
            step="1"
            class="h-8 rounded-sm bg-muted text-[13px]"
            :aria-label="t('minutes')"
          />
          <p class="text-center text-[10.5px] uppercase tracking-wide text-muted-foreground">
            {{ t("minutesShort") }}
          </p>
        </div>
        <div class="space-y-1">
          <Input
            v-model.number="durationSeconds"
            type="number"
            min="0"
            max="59"
            step="1"
            class="h-8 rounded-sm bg-muted text-[13px]"
            :aria-label="t('seconds')"
          />
          <p class="text-center text-[10.5px] uppercase tracking-wide text-muted-foreground">
            {{ t("secondsShort") }}
          </p>
        </div>
      </div>
    </EditorField>

    <EditorField :label="t('labelLabel')" :hint="t('labelHint')">
      <Input
        v-model="label"
        class="h-8 rounded-sm bg-muted text-[13px]"
        :placeholder="t('labelPlaceholder')"
      />
    </EditorField>

    <EditorField :label="t('displayLabel')">
      <Select
        :model-value="displayMode"
        @update:model-value="(value) => (displayMode = value as string)"
      >
        <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="label">{{ t("labelOnly") }}</SelectItem>
          <SelectItem value="progress">{{ t("labelAndProgress") }}</SelectItem>
        </SelectContent>
      </Select>
    </EditorField>

    <div class="rounded-sm border bg-muted/30 p-3">
      <p class="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {{ t("preview") }}
      </p>
      <div class="space-y-2">
        <div class="flex items-end justify-between gap-3">
          <span class="text-[12.5px] font-medium">{{ label || t("labelPlaceholder") }}</span>
          <span class="font-mono text-xl font-semibold tabular-nums">{{ previewTime }}</span>
        </div>
        <Progress v-if="displayMode === 'progress'" :model-value="mode === 'count-up' ? 0 : 100" class="h-2" />
      </div>
    </div>
  </EditorSection>

  <EditorSection
    :title="t('persistenceSection')"
    icon="lucide:save"
    accent="content"
    :default-open="false"
  >
    <EditorField :label="t('initialStatusLabel')" :hint="t('initialStatusHint')">
      <Select
        :model-value="initialStatus"
        @update:model-value="(value) => (initialStatus = value as string)"
      >
        <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="running">{{ t("startImmediately") }}</SelectItem>
          <SelectItem value="paused">{{ t("waitForStartRule") }}</SelectItem>
        </SelectContent>
      </Select>
    </EditorField>

    <EditorField :label="t('persistenceLabel')" :hint="t('persistenceHint')">
      <Select
        :model-value="persistenceMode"
        @update:model-value="(value) => (persistenceMode = value as string)"
      >
        <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="wall-clock">{{ t("wallClock") }}</SelectItem>
          <SelectItem value="active-session">{{ t("activeSession") }}</SelectItem>
        </SelectContent>
      </Select>
    </EditorField>
  </EditorSection>

  <EditorSection
    :title="t('completionSection')"
    icon="lucide:workflow"
    accent="content"
    :default-open="false"
  >
    <EditorField :label="t('validateFormLabel')">
      <div class="flex items-center justify-between gap-3">
        <span class="text-[12.5px] text-muted-foreground">{{ t("validateFormHint") }}</span>
        <Switch v-model="validateForm" />
      </div>
    </EditorField>

    <EditorField :label="t('automationPathLabel')" :hint="t('automationPathHint')">
      <EditorPathInput
        v-model="automationPath"
        mode="existing-file"
        :scopes="['site']"
        content-type-filter="automation"
        placeholder="/automations/on-timer-complete"
        aria-label="Timer automation path"
      />
    </EditorField>

    <EditorField :label="t('navigationPathLabel')" :hint="t('navigationPathHint')">
      <EditorPathInput
        v-model="navigationPath"
        mode="existing-file"
        :scopes="['site', 'external']"
        allow-http-urls
        placeholder="/next-page"
        aria-label="Timer navigation path"
      />
    </EditorField>

    <EditorField :label="t('redirectDelayLabel')" :hint="t('redirectDelayHint')">
      <Input
        v-model.number="redirectDelaySeconds"
        type="number"
        min="0"
        step="1"
        class="h-8 rounded-sm bg-muted text-[13px]"
      />
    </EditorField>
  </EditorSection>
</template>

<i18n lang="yaml">
en:
  timerSection: Timer
  modeLabel: Direction
  countdown: Count down
  countUp: Count up
  durationLabel: Duration
  durationHint: The timer completes after this configured duration.
  zeroDurationHint: A zero duration completes immediately.
  hours: Hours
  minutes: Minutes
  seconds: Seconds
  hoursShort: hr
  minutesShort: min
  secondsShort: sec
  labelLabel: Label
  labelHint: Supports template values from report and answer data.
  labelPlaceholder: Timer
  displayLabel: Display
  labelOnly: Label and time
  labelAndProgress: Label, time and progress
  preview: Preview
  persistenceSection: Persistence
  initialStatusLabel: Initial state
  initialStatusHint: Paused timers retain their progress and can be started by a conditional rule.
  startImmediately: Start immediately
  waitForStartRule: Paused until started
  persistenceLabel: Time source
  persistenceHint: Wall clock includes time away from the page; active session only advances while the page is open.
  wallClock: Wall clock
  activeSession: Active session
  completionSection: On completion
  validateFormLabel: Validate enclosing form
  validateFormHint: Run actions only when fields in a parent form are valid.
  automationPathLabel: Automation path
  automationPathHint: Optional automation to run once when the timer completes.
  navigationPathLabel: Navigation path
  navigationPathHint: Optional internal or external destination after completion.
  redirectDelayLabel: Redirect delay (seconds)
  redirectDelayHint: Wait after the automation finishes before navigating.
</i18n>
